import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../convex/_generated/api";
import { ConvexError } from "convex/values";
import styles from "./App.module.css";

// ─── Types ───────────────────────────────────────────────────────────────────
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  businessName: string;
  websiteUrl: string;
  role: string;
  useCase: string;
}

const ROLES = [
  "Founder / CEO",
  "CTO / Developer",
  "Product Manager",
  "Marketing / Growth",
  "Developer / Builder",
  "Other",
];

// ─── Scroll Reveal Hook ───────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add(styles.revealed); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// ─── SVG Illustrations ────────────────────────────────────────────────────────

/** Hero: animated Human → Agent → Business flow */
function HeroIllustration() {
  return (
    <svg viewBox="0 0 680 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.heroSvg}>
      <defs>
        <radialGradient id="hg1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#6ee7b7" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#6ee7b7" stopOpacity="0"/>
        </radialGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Human node */}
      <g className={styles.nodeHuman}>
        <circle cx="100" cy="140" r="52" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
        {/* person icon */}
        <circle cx="100" cy="122" r="14" fill="none" stroke="#7a798a" strokeWidth="1.5"/>
        <path d="M70 168c0-16.6 13.4-30 30-30s30 13.4 30 30" stroke="#7a798a" strokeWidth="1.5" strokeLinecap="round"/>
        <text x="100" y="208" textAnchor="middle" fill="#4a4858" fontSize="11" fontFamily="DM Sans, sans-serif">Human</text>
      </g>

      {/* Arrow 1 — animated dash */}
      <g className={styles.arrowFlow}>
        <line x1="158" y1="140" x2="248" y2="140" stroke="#6ee7b7" strokeWidth="1.5" strokeDasharray="6 4" strokeOpacity="0.5" className={styles.dashAnim1}/>
        <polygon points="248,135 258,140 248,145" fill="#6ee7b7" opacity="0.5"/>
        <text x="203" y="128" textAnchor="middle" fill="#4a4858" fontSize="10" fontFamily="DM Sans, sans-serif">Query</text>
      </g>

      {/* Agent node — glowing */}
      <g className={styles.nodeAgent}>
        <circle cx="340" cy="140" r="64" fill="url(#hg1)"/>
        <circle cx="340" cy="140" r="52" fill="rgba(110,231,183,0.06)" stroke="rgba(110,231,183,0.35)" strokeWidth="1.5"/>
        {/* agent bolt icon */}
        <path d="M348 112l-18 30h14l-6 26 20-34h-14l4-22z" fill="#6ee7b7" filter="url(#glow)"/>
        <text x="340" y="208" textAnchor="middle" fill="#6ee7b7" fontSize="11" fontFamily="DM Sans, sans-serif" opacity="0.8">AI Agent</text>
        {/* orbit ring */}
        <circle cx="340" cy="140" r="64" stroke="rgba(110,231,183,0.12)" strokeWidth="1" strokeDasharray="4 6" className={styles.orbitRing}/>
      </g>

      {/* Arrow 2 */}
      <g className={styles.arrowFlow}>
        <line x1="398" y1="140" x2="488" y2="140" stroke="#6ee7b7" strokeWidth="1.5" strokeDasharray="6 4" strokeOpacity="0.5" className={styles.dashAnim2}/>
        <polygon points="488,135 498,140 488,145" fill="#6ee7b7" opacity="0.5"/>
        <text x="443" y="128" textAnchor="middle" fill="#4a4858" fontSize="10" fontFamily="DM Sans, sans-serif">Structured</text>
        <text x="443" y="140" textAnchor="middle" fill="#4a4858" fontSize="10" fontFamily="DM Sans, sans-serif">Data</text>
      </g>

      {/* Business node */}
      <g className={styles.nodeBusiness}>
        <circle cx="580" cy="140" r="52" fill="rgba(255,255,255,0.03)" stroke="rgba(110,231,183,0.2)" strokeWidth="1"/>
        {/* building icon */}
        <rect x="563" y="118" width="34" height="36" rx="2" fill="none" stroke="#6ee7b7" strokeWidth="1.5" opacity="0.7"/>
        <rect x="569" y="124" width="8" height="8" rx="1" fill="#6ee7b7" opacity="0.4"/>
        <rect x="583" y="124" width="8" height="8" rx="1" fill="#6ee7b7" opacity="0.4"/>
        <rect x="569" y="136" width="8" height="8" rx="1" fill="#6ee7b7" opacity="0.4"/>
        <rect x="583" y="136" width="8" height="8" rx="1" fill="#6ee7b7" opacity="0.4"/>
        <rect x="574" y="148" width="12" height="6" rx="1" fill="#6ee7b7" opacity="0.6"/>
        <text x="580" y="208" textAnchor="middle" fill="#6ee7b7" fontSize="11" fontFamily="DM Sans, sans-serif" opacity="0.8">Your Business</text>
      </g>

      {/* AgentGateway label at bottom center */}
      <rect x="256" y="240" width="168" height="26" rx="13" fill="rgba(110,231,183,0.07)" stroke="rgba(110,231,183,0.2)" strokeWidth="1"/>
      <text x="340" y="257" textAnchor="middle" fill="#6ee7b7" fontSize="11" fontFamily="DM Sans, sans-serif" fontWeight="500" opacity="0.9">Powered by AgentGateway</text>
    </svg>
  );
}

/** Problem Today: human browsing */
function TodayIllustration() {
  return (
    <svg viewBox="0 0 280 160" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:"100%"}}>
      {/* browser window */}
      <rect x="20" y="20" width="240" height="120" rx="8" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
      {/* titlebar */}
      <rect x="20" y="20" width="240" height="28" rx="8" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
      <circle cx="38" cy="34" r="4" fill="rgba(239,68,68,0.5)"/>
      <circle cx="52" cy="34" r="4" fill="rgba(234,179,8,0.5)"/>
      <circle cx="66" cy="34" r="4" fill="rgba(34,197,94,0.5)"/>
      {/* url bar */}
      <rect x="82" y="27" width="120" height="14" rx="4" fill="rgba(255,255,255,0.06)"/>
      <text x="142" y="37" textAnchor="middle" fill="#4a4858" fontSize="8" fontFamily="JetBrains Mono, monospace">yoursite.com</text>
      {/* content lines */}
      <rect x="36" y="64" width="80" height="8" rx="2" fill="rgba(255,255,255,0.08)"/>
      <rect x="36" y="78" width="140" height="5" rx="2" fill="rgba(255,255,255,0.04)"/>
      <rect x="36" y="89" width="110" height="5" rx="2" fill="rgba(255,255,255,0.04)"/>
      <rect x="36" y="100" width="90" height="5" rx="2" fill="rgba(255,255,255,0.04)"/>
      {/* image placeholder */}
      <rect x="185" y="62" width="55" height="50" rx="4" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.07)" strokeWidth="1"/>
      <line x1="185" y1="62" x2="240" y2="112" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
      <line x1="240" y1="62" x2="185" y2="112" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
      {/* nav links — scattered, hard to parse */}
      <rect x="36" y="118" width="24" height="12" rx="3" fill="rgba(239,68,68,0.15)" stroke="rgba(239,68,68,0.2)" strokeWidth="1"/>
      <rect x="66" y="118" width="30" height="12" rx="3" fill="rgba(255,255,255,0.05)"/>
      <rect x="102" y="118" width="24" height="12" rx="3" fill="rgba(255,255,255,0.05)"/>
      <text x="48" y="127" textAnchor="middle" fill="rgba(239,68,68,0.6)" fontSize="7" fontFamily="DM Sans, sans-serif">noise</text>
      <text x="81" y="127" textAnchor="middle" fill="#4a4858" fontSize="7" fontFamily="DM Sans, sans-serif">ads</text>
      <text x="114" y="127" textAnchor="middle" fill="#4a4858" fontSize="7" fontFamily="DM Sans, sans-serif">nav</text>
    </svg>
  );
}

/** Problem Tomorrow: clean agent query */
function TomorrowIllustration() {
  return (
    <svg viewBox="0 0 280 160" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:"100%"}}>
      <defs>
        <radialGradient id="ag2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#6ee7b7" stopOpacity="0.12"/>
          <stop offset="100%" stopColor="#6ee7b7" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="140" cy="80" r="70" fill="url(#ag2)"/>
      {/* terminal window */}
      <rect x="24" y="24" width="232" height="112" rx="8" fill="rgba(9,9,15,0.9)" stroke="rgba(110,231,183,0.2)" strokeWidth="1"/>
      <rect x="24" y="24" width="232" height="24" rx="8" fill="rgba(110,231,183,0.05)" stroke="rgba(110,231,183,0.15)" strokeWidth="1"/>
      <circle cx="40" cy="36" r="4" fill="rgba(110,231,183,0.3)"/>
      <circle cx="54" cy="36" r="4" fill="rgba(110,231,183,0.15)"/>
      <circle cx="68" cy="36" r="4" fill="rgba(110,231,183,0.08)"/>
      <text x="40" y="58" fill="#4a4858" fontSize="8" fontFamily="JetBrains Mono, monospace">GET /query</text>
      <text x="40" y="71" fill="#6ee7b7" fontSize="8" fontFamily="JetBrains Mono, monospace" opacity="0.7">→ "Italian, $60, Friday 7pm"</text>
      <rect x="40" y="80" width="200" height="1" fill="rgba(255,255,255,0.05)"/>
      <text x="40" y="96" fill="#a78bfa" fontSize="8" fontFamily="JetBrains Mono, monospace">200 OK</text>
      <text x="40" y="109" fill="rgba(255,255,255,0.4)" fontSize="8" fontFamily="JetBrains Mono, monospace">{"{ name, price, available: true }"}</text>
      <text x="40" y="122" fill="#6ee7b7" fontSize="8" fontFamily="JetBrains Mono, monospace" className={styles.blinkCursor}>▋</text>
    </svg>
  );
}

/** Step 1: Onboarding form */
function Step1Illustration() {
  return (
    <svg viewBox="0 0 260 130" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:"100%", height:"100%"}}>
      <rect x="16" y="12" width="228" height="106" rx="8" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.07)" strokeWidth="1"/>
      <text x="28" y="30" fill="#6ee7b7" fontSize="9" fontFamily="DM Sans, sans-serif" opacity="0.7">Connect your business</text>
      {/* URL input */}
      <rect x="28" y="36" width="160" height="18" rx="4" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
      <text x="36" y="48" fill="#4a4858" fontSize="8" fontFamily="JetBrains Mono, monospace">https://yoursite.com</text>
      <rect x="194" y="36" width="36" height="18" rx="4" fill="rgba(110,231,183,0.15)" stroke="rgba(110,231,183,0.3)" strokeWidth="1"/>
      <text x="212" y="48" textAnchor="middle" fill="#6ee7b7" fontSize="8" fontFamily="DM Sans, sans-serif">Scan</text>
      {/* progress */}
      <rect x="28" y="64" width="204" height="4" rx="2" fill="rgba(255,255,255,0.05)"/>
      <rect x="28" y="64" width="140" height="4" rx="2" fill="rgba(110,231,183,0.4)" className={styles.progressBar}/>
      <text x="28" y="82" fill="#4a4858" fontSize="8" fontFamily="DM Sans, sans-serif">Extracting business data…</text>
      {/* fields filling in */}
      <rect x="28" y="88" width="60" height="8" rx="2" fill="rgba(110,231,183,0.12)" stroke="rgba(110,231,183,0.2)" strokeWidth="1"/>
      <rect x="94" y="88" width="60" height="8" rx="2" fill="rgba(110,231,183,0.12)" stroke="rgba(110,231,183,0.2)" strokeWidth="1"/>
      <rect x="160" y="88" width="60" height="8" rx="2" fill="rgba(110,231,183,0.12)" stroke="rgba(110,231,183,0.2)" strokeWidth="1"/>
      <text x="58" y="94" textAnchor="middle" fill="#6ee7b7" fontSize="7" fontFamily="DM Sans, sans-serif" opacity="0.6">Hours</text>
      <text x="124" y="94" textAnchor="middle" fill="#6ee7b7" fontSize="7" fontFamily="DM Sans, sans-serif" opacity="0.6">Pricing</text>
      <text x="190" y="94" textAnchor="middle" fill="#6ee7b7" fontSize="7" fontFamily="DM Sans, sans-serif" opacity="0.6">Services</text>
      <text x="28" y="112" fill="#4a4858" fontSize="8" fontFamily="DM Sans, sans-serif">✓ 14 fields extracted</text>
    </svg>
  );
}

/** Step 2: Structured schema */
function Step2Illustration() {
  return (
    <svg viewBox="0 0 260 130" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:"100%", height:"100%"}}>
      <rect x="16" y="12" width="228" height="106" rx="8" fill="rgba(9,9,15,0.8)" stroke="rgba(110,231,183,0.15)" strokeWidth="1"/>
      <text x="28" y="30" fill="#a78bfa" fontSize="8" fontFamily="JetBrains Mono, monospace">{"{"}</text>
      <text x="40" y="43" fill="#4a4858" fontSize="8" fontFamily="JetBrains Mono, monospace">"name": <tspan fill="rgba(110,231,183,0.7)">"Mario's Italian"</tspan></text>
      <text x="40" y="56" fill="#4a4858" fontSize="8" fontFamily="JetBrains Mono, monospace">"cuisine": <tspan fill="rgba(110,231,183,0.7)">"Italian"</tspan></text>
      <text x="40" y="69" fill="#4a4858" fontSize="8" fontFamily="JetBrains Mono, monospace">"priceRange": <tspan fill="rgba(110,231,183,0.7)">"$40–60"</tspan></text>
      <text x="40" y="82" fill="#4a4858" fontSize="8" fontFamily="JetBrains Mono, monospace">"available": <tspan fill="#a78bfa">true</tspan></text>
      <text x="40" y="95" fill="#4a4858" fontSize="8" fontFamily="JetBrains Mono, monospace">"actions": <tspan fill="#a78bfa">["book","menu"]</tspan></text>
      <text x="28" y="108" fill="#a78bfa" fontSize="8" fontFamily="JetBrains Mono, monospace">{"}"}</text>
      {/* glow on the right edge */}
      <rect x="226" y="12" width="18" height="106" rx="0" fill="url(#schemaGlow)"/>
      <defs>
        <linearGradient id="schemaGlow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#6ee7b7" stopOpacity="0"/>
          <stop offset="100%" stopColor="#6ee7b7" stopOpacity="0.08"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Step 3: Agent result */
function Step3Illustration() {
  return (
    <svg viewBox="0 0 260 130" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:"100%", height:"100%"}}>
      <defs>
        <radialGradient id="rg3" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#6ee7b7" stopOpacity="0.1"/>
          <stop offset="100%" stopColor="#6ee7b7" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="130" cy="65" r="60" fill="url(#rg3)"/>
      {/* chat bubble */}
      <rect x="20" y="14" width="220" height="30" rx="8" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      <text x="30" y="33" fill="#7a798a" fontSize="9" fontFamily="DM Sans, sans-serif">Find me Italian dinner under $60 on Friday…</text>
      {/* result card - highlighted */}
      <rect x="20" y="54" width="220" height="42" rx="8" fill="rgba(110,231,183,0.07)" stroke="rgba(110,231,183,0.3)" strokeWidth="1.5"/>
      <circle cx="36" cy="68" r="7" fill="rgba(110,231,183,0.2)"/>
      <path d="M32 68l3 3 5-5" stroke="#6ee7b7" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <text x="50" y="66" fill="#f0eff8" fontSize="9" fontFamily="DM Sans, sans-serif" fontWeight="600">Mario's Italian</text>
      <text x="50" y="78" fill="#7a798a" fontSize="8" fontFamily="DM Sans, sans-serif">$40–60 · Available Fri 7pm · Book now</text>
      <rect x="174" y="59" width="54" height="16" rx="4" fill="rgba(110,231,183,0.2)" stroke="rgba(110,231,183,0.4)" strokeWidth="1"/>
      <text x="201" y="70" textAnchor="middle" fill="#6ee7b7" fontSize="8" fontFamily="DM Sans, sans-serif">Book →</text>
      {/* dimmed result */}
      <rect x="20" y="102" width="220" height="20" rx="6" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
      <text x="30" y="115" fill="#2a2835" fontSize="8" fontFamily="DM Sans, sans-serif">Another restaurant (not on AgentGateway)</text>
    </svg>
  );
}

// ─── Feature SVG Icons ────────────────────────────────────────────────────────
const featureIcons: Record<string, JSX.Element> = {
  extraction: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M11 2L19 6V11C19 15.4 15.9 19.5 11 21C6.1 19.5 3 15.4 3 11V6L11 2Z" stroke="#6ee7b7" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M8 11l2 2 4-4" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  schema: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="2" y="5" width="8" height="6" rx="2" stroke="#6ee7b7" strokeWidth="1.5"/>
      <rect x="12" y="2" width="8" height="6" rx="2" stroke="#6ee7b7" strokeWidth="1.5"/>
      <rect x="12" y="14" width="8" height="6" rx="2" stroke="#6ee7b7" strokeWidth="1.5"/>
      <path d="M10 8h2V5M10 8h2v9" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  sync: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M4 11A7 7 0 0118 7" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M18 11A7 7 0 014 15" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M15 5l3 2-2 3M7 17l-3-2 2-3" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  action: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M13 2L7 12h5l-2 8 7-10h-5l1-8z" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  analytics: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M3 17l4-5 4 2 4-6 4 4" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 20h16" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  api: (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M7 8l-4 3 4 3M15 8l4 3-4 3M13 5l-4 12" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

// ─── Components ───────────────────────────────────────────────────────────────

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <nav className={`${styles.navbar} ${scrolled ? styles.navbarScrolled : ""}`}>
      <div className={styles.navInner}>
        <div className={styles.logo}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L21 7V17L12 22L3 17V7Z" fill="rgba(110,231,183,0.2)" stroke="#6ee7b7" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M13 6L8 13h4l-1 5 6-8h-4l1-4z" fill="#6ee7b7"/>
          </svg>
          <span className={styles.logoText}>AgentGateway</span>
        </div>
        <a href="#waitlist" className={styles.navCta}>Join Waitlist</a>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className={styles.hero}>
      <div className={styles.heroDotGrid} />
      <div className={styles.heroGlow1} />
      <div className={styles.heroGlow2} />
      <div className={styles.heroContent}>
        <div className={styles.heroBadge}>
          <span className={styles.badgeDot} />
          <span>Early access · Now open</span>
        </div>
        <h1 className={styles.heroTitle}>
          Your business,<br />
          <em>visible to AI agents.</em>
        </h1>
        <p className={styles.heroSubtitle}>
          The web is shifting. AI agents are replacing browsers as the way people
          discover and transact with businesses. AgentGateway makes your business
          structured, discoverable, and actionable — by any AI.
        </p>
        <div className={styles.heroCtas}>
          <a href="#waitlist" className={styles.btnPrimary}>
            Get early access
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
          <a href="#how-it-works" className={styles.btnGhost}>See how it works</a>
        </div>
        <div className={styles.heroMockupWrapper}>
          <div className={styles.heroMockup}>
            <div className={styles.mockupTitlebar}>
              <span className={styles.mockupDot} style={{background:"rgba(239,68,68,0.5)"}}/>
              <span className={styles.mockupDot} style={{background:"rgba(234,179,8,0.5)"}}/>
              <span className={styles.mockupDot} style={{background:"rgba(34,197,94,0.5)"}}/>
              <span className={styles.mockupUrl}>agentgateway.io/query</span>
            </div>
            <div className={styles.mockupBody}>
              <HeroIllustration />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProblemSection() {
  const ref = useReveal();
  return (
    <section className={styles.problem} id="problem">
      <div className={styles.container}>
        <div ref={ref} className={styles.reveal}>
          <div className={styles.sectionLabel}>The Shift</div>
          <h2 className={styles.sectionTitle}>
            The way people find businesses<br />is changing permanently.
          </h2>
        </div>
        <div className={styles.splitGrid}>
          <div className={`${styles.splitCard} ${styles.splitBad}`}>
            <div className={styles.splitCardLabel}>Today</div>
            <h3>Humans browse websites</h3>
            <p>Your SEO, your design, your navigation — all optimized for human eyes scanning a page.</p>
            <div className={styles.splitIllustration}><TodayIllustration /></div>
          </div>
          <div className={styles.splitDivider}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M6 16h20M20 10l6 6-6 6" stroke="rgba(110,231,183,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className={`${styles.splitCard} ${styles.splitGood}`}>
            <div className={styles.splitCardLabel}>Tomorrow</div>
            <h3>AI agents act on behalf of humans</h3>
            <p>Agents don't scroll. They query structured data, compare options, and complete transactions — without a human touching the browser.</p>
            <div className={styles.splitIllustration}><TomorrowIllustration /></div>
          </div>
        </div>
        <p className={styles.problemCta}>
          If your business isn't readable by an AI agent,<br />
          <strong>it's invisible to them.</strong>
        </p>
      </div>
    </section>
  );
}

function HowItWorks() {
  const ref = useReveal();
  const steps = [
    { num: "01", title: "Register your business", desc: "Connect your website or fill out your business profile. Our AI extracts and structures your key information automatically.", Illustration: Step1Illustration },
    { num: "02", title: "We structure your data", desc: "Your services, products, pricing, availability, and policies get organized into a clean, AI-readable schema.", Illustration: Step2Illustration },
    { num: "03", title: "AI agents discover you", desc: "When someone asks their AI assistant for what you offer, you appear — with all the right context for the agent to act.", Illustration: Step3Illustration },
  ];
  return (
    <section className={styles.howItWorks} id="how-it-works">
      <div className={styles.container}>
        <div ref={ref} className={styles.reveal}>
          <div className={styles.sectionLabel}>How It Works</div>
          <h2 className={styles.sectionTitle}>Three steps to agent-readiness.</h2>
        </div>
        <div className={styles.stepsGrid}>
          {steps.map((step, i) => (
            <div key={step.num} className={styles.stepCard} style={{animationDelay: `${i * 0.12}s`}}>
              <div className={styles.stepNum}>{step.num}</div>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDesc}>{step.desc}</p>
              <div className={styles.stepIllustration}>
                <step.Illustration />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const ref = useReveal();
  const features = [
    { key: "extraction", title: "Auto-extraction", desc: "Paste your URL. Our AI reads your site and structures your data — no manual entry required." },
    { key: "schema",     title: "Universal schema", desc: "One standardized format any AI agent can query. Works with ChatGPT, Claude, Gemini, and any future agent." },
    { key: "sync",       title: "Live sync", desc: "Your prices change, your hours update — AgentGateway keeps your agent-facing data fresh automatically." },
    { key: "action",     title: "Action layer", desc: "Beyond discovery: agents can book, purchase, and transact directly through your gateway." },
    { key: "analytics",  title: "Analytics", desc: "See which agents query your business, what they're looking for, and where you lose them." },
    { key: "api",        title: "Developer API", desc: "Building an agent? Query the entire gateway with one endpoint. Rich filters, structured responses." },
  ];
  return (
    <section className={styles.features} id="features">
      <div className={styles.container}>
        <div ref={ref} className={styles.reveal}>
          <div className={styles.sectionLabel}>Features</div>
          <h2 className={styles.sectionTitle}>Everything your business needs<br />to thrive in the agentic web.</h2>
        </div>
        <div className={styles.featuresGrid}>
          {features.map((f) => (
            <div key={f.key} className={styles.featureCard}>
              <div className={styles.featureIconWrap}>{featureIcons[f.key]}</div>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WaitlistForm() {
  const joinWaitlist = useMutation(api.waitlist.joinWaitlist);
  const count = useQuery(api.waitlist.getWaitlistCount);
  const ref = useReveal();

  const [form, setForm] = useState<FormData>({ firstName:"", lastName:"", email:"", businessName:"", websiteUrl:"", role:"", useCase:"" });
  const [status, setStatus] = useState<"idle"|"loading"|"success"|"error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.businessName) {
      setErrorMsg("Please fill in all required fields."); return;
    }
    setStatus("loading"); setErrorMsg("");
    try {
      await joinWaitlist({ firstName:form.firstName, lastName:form.lastName, email:form.email, businessName:form.businessName, websiteUrl:form.websiteUrl||undefined, role:form.role||undefined, useCase:form.useCase||undefined });
      setStatus("success");
    } catch (err: unknown) {
      setStatus("error");
      const raw = err instanceof ConvexError ? String(err.message) : err instanceof Error ? err.message : "";
      const match = raw.match(/ConvexError:\s*(.+?)(?:\s+at\s+|\s*$)/);
      const clean = match ? match[1].trim() : raw.length < 120 ? raw : "Something went wrong. Please try again.";
      setErrorMsg(clean || "Something went wrong. Please try again.");
    }
  };

  return (
    <section className={styles.waitlistSection} id="waitlist">
      <div className={styles.waitlistGlow} />
      <div className={styles.container}>
        <div className={styles.waitlistGrid}>
          <div ref={ref} className={`${styles.waitlistLeft} ${styles.reveal}`}>
            <div className={styles.sectionLabel}>Early Access</div>
            <h2 className={styles.waitlistTitle}>Be first.<br />Shape the platform.</h2>
            <p className={styles.waitlistDesc}>
              We're onboarding a small group of early partners to build AgentGateway with.
              You'll get free access, direct input on features, and a permanent advantage as the agentic web takes shape.
            </p>
            <div className={styles.waitlistStats}>
              {count !== undefined && (
                <div className={styles.stat}>
                  <span className={styles.statNum}>{count}+</span>
                  <span className={styles.statLabel}>businesses on waitlist</span>
                </div>
              )}
              <div className={styles.stat}>
                <span className={styles.statNum}>Free</span>
                <span className={styles.statLabel}>during early access</span>
              </div>
            </div>
          </div>
          <div className={styles.waitlistRight}>
            {status === "success" ? (
              <div className={styles.successState}>
                <div className={styles.successIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>You're on the list.</h3>
                <p>We'll be in touch as we onboard early partners. Expect something good.</p>
              </div>
            ) : (
              <div className={styles.formCard}>
                <h3 className={styles.formTitle}>Request early access</h3>
                <div className={styles.formRow}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>First name <span className={styles.required}>*</span></label>
                    <input className={styles.input} name="firstName" placeholder="Jane" value={form.firstName} onChange={handleChange}/>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.label}>Last name <span className={styles.required}>*</span></label>
                    <input className={styles.input} name="lastName" placeholder="Smith" value={form.lastName} onChange={handleChange}/>
                  </div>
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>Email <span className={styles.required}>*</span></label>
                  <input className={styles.input} name="email" type="email" placeholder="jane@company.com" value={form.email} onChange={handleChange}/>
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>Business name <span className={styles.required}>*</span></label>
                  <input className={styles.input} name="businessName" placeholder="Acme Inc." value={form.businessName} onChange={handleChange}/>
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>Website URL</label>
                  <input className={styles.input} name="websiteUrl" placeholder="https://yoursite.com" value={form.websiteUrl} onChange={handleChange}/>
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>Your role</label>
                  <select className={styles.select} name="role" value={form.role} onChange={handleChange}>
                    <option value="">Select a role…</option>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>How do you plan to use AgentGateway?</label>
                  <textarea className={styles.textarea} name="useCase" placeholder="Tell us about your business and what you'd like AI agents to be able to do with it…" value={form.useCase} onChange={handleChange} rows={3}/>
                </div>
                {errorMsg && <p className={styles.errorMsg}>{errorMsg}</p>}
                <button className={styles.submitBtn} onClick={handleSubmit} disabled={status==="loading"}>
                  {status === "loading" ? <span className={styles.spinner}/> : (
                    <>Request early access
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </>
                  )}
                </button>
                <p className={styles.formFootnote}>No spam. No commitment. Unsubscribe anytime.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.footerInner}>
          <div className={styles.logo}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L21 7V17L12 22L3 17V7Z" fill="rgba(110,231,183,0.2)" stroke="#6ee7b7" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M13 6L8 13h4l-1 5 6-8h-4l1-4z" fill="#6ee7b7"/>
            </svg>
            <span className={styles.logoText}>AgentGateway</span>
          </div>
          <p className={styles.footerTagline}>Infrastructure for the agentic web.</p>
          <p className={styles.footerCopy}>© {new Date().getFullYear()} AgentGateway. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <div className={styles.app}>
      <Navbar />
      <main>
        <Hero />
        <ProblemSection />
        <HowItWorks />
        <Features />
        <WaitlistForm />
      </main>
      <Footer />
    </div>
  );
}
